<template>
    <div class="participation form-dimensioning">

        <div class="participation__step-indicator">
            <span
                v-for="(step, stepIndex) in getSteps"
                :key="`step-indicator-${stepIndex}`"
                :class="{
                    'participation__step-item': true,
                    'participation__step-item--hidden': step.hasOwnProperty('hidden') && step.hidden,
                    'participation__step-item--active': stepIndex == currentStepIndex
                }"
                @click="() => switchToStep(stepIndex)"
            >{{ stepIndex + 1 }}</span>
        </div>

        <div class="participation__form">
            <span class="participation__step-headline">{{ getCurrentStep.title }}</span>
            <span v-if="getCurrentStep.note" class="participation__step-note">{{ t(getCurrentStep.note) }}</span>

            <div class="participation__fields">
                <template v-if="['ticket', 'personal', 'travel'].indexOf(getCurrentStep.type) !== -1">
                    <transition-group name="participation__field-transition">
                        <template v-for="(field, fieldIndex) in getCurrentStep.fields">
                            <button-choice-element
                                v-if="field.type == 'buttonchoice' && conditionsEvaluate(field.conditions) && fieldEnabled(field.enabled || null)"
                                :name="field.handle"
                                :key="`participation-field-${fieldIndex}`"
                                v-model="form[field.handle]"
                                :choices="field.options"
                                :label="field.title" />

                            <lightswitch-element
                                v-if="field.type == 'lightswitch' && conditionsEvaluate(field.conditions) && fieldEnabled(field.enabled || null)"
                                :name="field.handle"
                                :key="`participation-field-${fieldIndex}`"
                                v-model="form[field.handle]"
                                :label="field.title" />

                            <input-element
                                v-else-if="field.type == 'text' && conditionsEvaluate(field.conditions)"
                                :name="field.handle"
                                :key="`participation-field-${fieldIndex}`"
                                v-model="form[field.handle]"
                                :disabled="field.hasOwnProperty('readonly') && field.readonly"
                                :placeholder="field.title" />

                            <select-element
                                v-else-if="field.type == 'options' && conditionsEvaluate(field.conditions)"
                                :name="field.handle"
                                :key="`participation-field-${fieldIndex}`"
                                :selected-value="form[field.handle]"
                                @change="e => form[field.handle] = e.id"
                                :disabled="field.hasOwnProperty('readonly') && field.readonly"
                                :placeholder="field.title"
                                :items="field.options" />
                        </template>
                    </transition-group>
                </template>

                <template v-else-if="isLastStep">
                    <span class="participation__done-headline">{{ t('vielen-dank') }}</span>
                    <p v-if="showTicketsBooked">
                        {{ ticketsBookedMessage }}
                    </p>
                    <div class="buttons">
                        <button-element
                            @click="handleClose"
                            :button="{
                                'label': t('schliessen'),
                            }"
                        ></button-element>
                    </div>
                </template>
            </div>
        </div>

        <div class="participation__reserved" v-if="form.participantTickets && form.participantTickets > 0">
            <span class="participation__reserved-headline">{{ t('tickets-reserved-headline') }}</span>
            <span class="participation__reserved-message" v-html="participantTicketsMessage" />
        </div>

        <div class="participation__buttons">

            <button-element
                v-if="!isLastStep"
                @click="handleNextStep"
                :button="{
                    'label': t('weiter'),
                }"
                :disabled="isSaving || !conditionsMetForCurrentStep"
            >
                <span slot="icon" v-if="isSaving">
                    <loader-element />
                </span>
            </button-element>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import store from "@/store";
import {
    ME_POST_NS,
} from "@/store/actions.type";

import InputElement from '@/components/elements/form/InputElement.vue';
import LightswitchElement from '@/components/elements/form/LightswitchElement.vue';
import SelectElement from '@/components/elements/form/SelectElement.vue';
import ButtonChoiceElement from '@/components/elements/form/ButtonChoiceElement.vue';
import LoaderElement from '@/components/elements/LoaderElement.vue';

export default {
    name: 'ParticipationElement',
    components: {
        InputElement,
        LightswitchElement,
        SelectElement,
        LoaderElement,
        ButtonChoiceElement,
    },
    props: {
        participation: {
            type: Object,
            required: true
        },
    },
    computed: {

        ...mapGetters('uitexts', {
            t: 't'
        }),

        ...mapState('me', {
            isSaving: 'isSaving',
            form: 'me'
        }),

        getSteps() {

            const actualSteps = this.participation.steps.filter((step) => {
                const canBeDisabled = step.hasOwnProperty('enabled');

                if (canBeDisabled) {
                    return this.fieldEnabled(step.enabled);
                }

                return true;
            });

            return [
                ...actualSteps,
                {
                    hidden: true,
                    type: 'finish'
                }
            ]
        },

        getCurrentStep() {
            return this.getSteps[this.currentStepIndex];
        },

        isLastStep() {
            return this.getCurrentStep.type == 'finish';
        },

        conditionsMetForCurrentStep() {

            if (this.getCurrentStep.hasOwnProperty('conditions') &&
                this.getCurrentStep.conditions != null
            ) {
                return this.conditionsEvaluate(this.getCurrentStep.conditions);
            }

            return true;
        },

        showTicketsBooked() {
            return this.result && this.result.hasOwnProperty('numberOfTicketsBooked') && this.result.numberOfTicketsBooked > 0;
        },

        ticketsBookedMessage() {
            return this.t('tickets-booked-message').replace('{anzahl_tickets}', this.result.numberOfTicketsBooked);
        },

        participantTicketsMessage() {
            return this.t('tickets-message').replace('{anzahl_tickets}', this.form.participantTickets);
        },
    },

    data: () => {
        return {
            currentStepIndex: 0,
            result: null
        }
    },

    async created() {

    },

    mounted() {

    },

    methods: {

        handleClose() {
            this.$emit('close');
        },

        switchToStep(index) {
            if (!this.conditionsMetForStepIndex(index)) { return }

            this.currentStepIndex = index;
        },

        async handleNextStep() {


            if (this.currentStepIndex + 1  == this.getSteps.length - 1) {
                this.result = await store.dispatch(ME_POST_NS);

                if (this.result.success) {
                    this.currentStepIndex++;
                }
            } else {
                this.currentStepIndex++;
            }
        },

        conditionsEvaluate(conditions) {

            if (!conditions) { return true; }

            let evaluates = true;
            conditions.forEach(condition => {
                const currentConditionEvaluates =
                    this.form.hasOwnProperty(condition) &&
                    (this.form[condition] === true || this.form[condition] == 'yes' || this.form[condition] == 'no');
                evaluates = evaluates && currentConditionEvaluates;
            });

            return evaluates;
        },

        fieldEnabled(conditions) {

            if (!conditions) { return true; }

            if (typeof conditions == 'boolean') {
                return !conditions;
            }

            let result = true;
            Object.keys(conditions).forEach((prop) => {
                const condition = conditions[prop];
                result = result && (this.form[prop] === condition);
            });

            return result;
        },

        conditionsMetForStepIndex(index) {

            if (this.getSteps[index].hasOwnProperty('conditions') &&
                this.getSteps[index].conditions != null
            ) {
                return this.conditionsEvaluate(this.getSteps[index].conditions);
            }

            return true;
        },
    }
}
</script>
